import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  const goToElektrik = () => {
    navigate('/elektrik');
    setMobileMenuOpen(false);
  };

  const goToWaermepumpe = () => {
    navigate('/waermepumpe');
    setMobileMenuOpen(false);
  };

  const goToKlimaanlagen = () => {
    navigate('/klimaanlagen');
    setMobileMenuOpen(false);
  };

  const goToDachdecker = () => {
    navigate('/dachdecker');
    setMobileMenuOpen(false);
  };

  const goToPartner = () => {
    navigate('/partner');
    setMobileMenuOpen(false);
  };

  const goToWallbox = () => {
    navigate('/wallbox');
    setMobileMenuOpen(false);
  };

  const goToPhotovoltaik = () => {
    navigate('/photovoltaik');
    setMobileMenuOpen(false);
  };

  const goToIndustrie = () => {
    navigate('/industrie');
    setMobileMenuOpen(false);
  };

  const goToBeratung = () => {
    navigate('/beratung');
    setMobileMenuOpen(false);
  };

  const goToHome = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 sm:h-24 md:h-32 lg:h-40">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={goToHome}>
            <img 
              src="https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/ocvvkw7r_IMG_0068.png"
              alt="Gravante Solar Logo"
              className="h-16 sm:h-20 md:h-28 lg:h-36 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4">
            <button onClick={goToPhotovoltaik} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('photovoltaik')}
            </button>
            <button onClick={goToIndustrie} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
              {t('industrie')}
            </button>
            <button onClick={goToWaermepumpe} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('waermepumpe')}
            </button>
            <button onClick={goToKlimaanlagen} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              Klimaanlagen
            </button>
            <button onClick={goToDachdecker} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('dachdecker')}
            </button>
            <button onClick={goToWallbox} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('wallbox')}
            </button>
            <button onClick={goToElektrik} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('elektrik')}
            </button>
            <button onClick={goToPartner} className="text-gray-700 hover:text-sky-600 transition-colors font-medium">
              {t('partner')}
            </button>
          </nav>

          {/* Language Switcher & CTA Button */}
          <div className="hidden lg:flex items-center gap-3 ml-4">
            <LanguageSwitcher />
            <Button onClick={goToBeratung} className="bg-sky-600 hover:bg-sky-700 text-white">
              {t('beratungAnfragen')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-gray-700 hover:text-sky-600 transition-colors rounded-lg hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 bg-white">
            <nav className="flex flex-col space-y-3">
              <button onClick={goToPhotovoltaik} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('photovoltaik')}
              </button>
              <button onClick={goToIndustrie} className="text-left py-2 text-gray-700 hover:text-amber-600 transition-colors font-medium">
                {t('industrie')}
              </button>
              <button onClick={goToWaermepumpe} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('waermepumpe')}
              </button>
              <button onClick={goToKlimaanlagen} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                Klimaanlagen
              </button>
              <button onClick={goToDachdecker} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('dachdecker')}
              </button>
              <button onClick={goToWallbox} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('wallbox')}
              </button>
              <button onClick={goToElektrik} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('elektrik')}
              </button>
              <button onClick={goToPartner} className="text-left py-2 text-gray-700 hover:text-sky-600 transition-colors font-medium">
                {t('partner')}
              </button>
              <div className="pt-3 mt-2 border-t border-gray-200">
                <Button onClick={goToBeratung} className="bg-sky-600 hover:bg-sky-700 text-white w-full py-3">
                  {t('beratungAnfragen')}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;